import { BaseRestDataProvider } from './BaseRestDataProvider';
import { SupplierInvoice } from '../models/SupplierInvoice';
import { toast } from 'react-toastify';

export interface CsvImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export class SupplierInvoiceRestDataProvider extends BaseRestDataProvider {
  constructor() {
    super('/v1/supplier-invoices');
  }

  getAll(): Promise<SupplierInvoice[]> {
    return this.get<SupplierInvoice[]>(``);
  }

  getById(id: string): Promise<SupplierInvoice> {
    return this.get<SupplierInvoice>(`/${id}`);
  }

  add(invoice: SupplierInvoice): Promise<SupplierInvoice> {
    return this.post<SupplierInvoice>(``, invoice);
  }

  update(id: string, invoice: SupplierInvoice): Promise<void> {
    return this.put<void>(`/${id}`, invoice);
  }

  delete(id: string): Promise<void> {
    return super.delete(`/${id}`);
  }

  book(id: string, costLedgerId: string, entryDate?: string): Promise<void> {
    return this.postVoid(`/${id}/book`, { costLedgerId, ...(entryDate ? { entryDate } : {}) });
  }

  unbook(id: string): Promise<void> {
    return this.postVoid(`/${id}/unbook`, {});
  }

  bookPayment(id: string, paymentDate: string, bankAccountId: string, isCreditNote = false): Promise<void> {
    return this.postVoid(`/${id}/book-payment`, { paymentDate, bankAccountId, isCreditNote });
  }

  async importCsv(
    file: File,
    mapping: Record<string, string>,
    delimiter: string = ',',
  ): Promise<CsvImportResult> {
    const token = BaseRestDataProvider.getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('delimiter', delimiter);

    const response = await fetch(`${this.baseUrl}/import-csv`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      toast.error('Could not import CSV file');
      throw new Error(errorText);
    }

    const result: CsvImportResult = await response.json();
    const skippedMsg = result.skipped > 0 ? `, ${result.skipped} row(s) skipped` : '';
    toast.success(`CSV imported — ${result.imported} invoice(s) added${skippedMsg}`);
    return result;
  }
}

export type { SupplierInvoice };


